import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GitCompare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n";
import { startComparison } from "../../services/Comparisons";
import { startErrorMessage } from "./compare-utils";
import { listStandardAgreements, StandardAgreement } from "../../services/StandardAgreements";

const AUTO_ROLE = "auto";
const ROLES = [AUTO_ROLE, "customer", "supplier", "discloser", "recipient", "mutual"];

/** Compare a file that is already in a project with one of the standards */
const CompareFileDialog: React.FC<{
  file: { id: string; name: string; projectId?: string } | null;
  onClose: () => void;
  /** Called with the new comparison; default: open its page */
  onStarted?: (comparisonId: string) => void;
}> = ({ file, onClose, onStarted }) => {
  const { t, locale } = useI18n();
  const c = t.comparisons;
  const navigate = useNavigate();
  const [standards, setStandards] = useState<StandardAgreement[] | null>(null);
  const [standardId, setStandardId] = useState("");
  const [role, setRole] = useState(AUTO_ROLE);
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) return;
    setStandardId("");
    setRole(AUTO_ROLE);
    setFocus("");
    setStandards(null);
    let cancelled = false;
    listStandardAgreements()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setStandards(res.data);
          // A single standard needs no choosing
          const usable = res.data.filter((s) => s.currentVersion?.status === "ready");
          if (usable.length === 1) setStandardId(usable[0].id);
        } else {
          setStandards([]);
          toast.error(c.standardsFailed);
        }
      })
      .catch(() => !cancelled && setStandards([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id]);

  const submit = async () => {
    if (!file || !standardId) return;
    setBusy(true);
    try {
      const res = await startComparison({
        standardAgreementId: standardId,
        receivedFileId: file.id,
        ourRole: role === AUTO_ROLE ? undefined : role,
        focus,
        language: locale === "sv" ? "sv" : "en",
      });
      if (res.success && res.data) {
        onClose();
        if (onStarted) onStarted(res.data.id);
        else navigate(`/comparisons/${res.data.id}`);
      } else {
        toast.error(startErrorMessage(res, c));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={file != null} onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="break-words">{file ? c.fileDialogTitle(file.name) : ""}</DialogTitle>
          <DialogDescription>{c.fileDialogDescription}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cmpf-standard">{c.pickStandard}</Label>
            {standards && standards.length === 0 ? (
              <p className="text-sm text-muted-foreground">{c.noStandards}</p>
            ) : (
              <Select value={standardId} onValueChange={setStandardId} disabled={!standards}>
                <SelectTrigger id="cmpf-standard">
                  <SelectValue placeholder={c.pickStandardPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {(standards ?? []).map((s) => {
                    const status = s.currentVersion?.status;
                    const unusable = status !== "ready";
                    return (
                      <SelectItem key={s.id} value={s.id} disabled={unusable}>
                        {s.name}
                        {unusable ? ` (${status === "preparing" || status === "pending" ? c.prep.preparing : c.prep.failed})` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cmpf-role">{c.ourRole}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="cmpf-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {c.roles[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cmpf-focus">{c.focus}</Label>
            <Textarea
              id="cmpf-focus"
              rows={2}
              maxLength={2000}
              value={focus}
              placeholder={c.focusPlaceholder}
              onChange={(e) => setFocus(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            {c.cancel}
          </Button>
          <Button disabled={!standardId || busy} onClick={() => void submit()}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GitCompare className="mr-2 h-4 w-4" />}
            {busy ? c.starting : c.start}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompareFileDialog;
